import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, CreditCard, Pencil, Plus, Trash2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { Service } from '../../../../../packages/contracts/src/index.js';
import { ActionMenu } from '../../components/ui/action-menu';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Select } from '../../components/ui/field';
import { PaginationControls, useControlledPagination } from '../../components/ui/pagination';
import { SearchBox } from '../../components/ui/search';
import { EmptyState, ErrorState, LoadingState } from '../../components/ui/state';
import { Table, Td, Th } from '../../components/ui/table';
import { ConfirmDialog, type ConfirmState } from '../../components/ui/confirm';
import { ListDateFilters } from '../../components/ui/list-date-filters';
import { api } from '../../shared/api';
import { formatDateTime } from '../../shared/format';
import { labelForServiceCategory } from '../../shared/i18n';
import { dateRangeForPreset, overlapsDateRange, useUrlListFilters } from '../../shared/listFilters';
import { matchesSearch } from '../../shared/search';
import { useAuth } from '../auth/AuthProvider';

const serviceCategories: Service['category'][] = ['grooming', 'spa', 'daycare', 'boarding', 'transportation', 'medical', 'treats', 'fee', 'other'];
const groomingCategories = serviceCategories.filter((category) => category !== 'transportation');
const transportCategories: Service['category'][] = ['transportation'];
const statusLabels: Record<string, string> = {
  scheduled: 'Programada',
  done: 'Terminada',
  cancelled: 'Cancelada',
};

export function AppointmentsPage() {
  return <AppointmentsListPage mode="grooming" />;
}

export function TransportAppointmentsPage() {
  return <AppointmentsListPage mode="transportation" />;
}

function AppointmentsListPage({ mode }: { readonly mode: 'grooming' | 'transportation' }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isTransportMode = mode === 'transportation';
  const visibleCategories = isTransportMode ? transportCategories : groomingCategories;
  const defaultRange = useMemo(() => dateRangeForPreset('today'), []);
  const { values: filters, setValue, setValues } = useUrlListFilters({ q: '', status: '', category: '', range: 'today', from: defaultRange.from, to: defaultRange.to, page: '1', pageSize: '10' }, { keepDefaults: true });
  const search = filters.q;
  const statusFilter = filters.status;
  const categoryFilter = visibleCategories.includes(filters.category as Service['category']) ? filters.category : '';
  const page = Math.max(1, Number(filters.page) || 1);
  const pageSize = Math.max(10, Number(filters.pageSize) || 10);
  const [confirm, setConfirm] = useState<ConfirmState | null>(null);

  const appointmentListOptions = { limit: page * pageSize + 1, q: search, status: statusFilter, category: isTransportMode ? 'transportation' : categoryFilter, from: filters.from, to: filters.to };
  const appointmentsQuery = useQuery({ queryKey: ['appointments', appointmentListOptions], queryFn: () => api.appointments.list(appointmentListOptions) });
  const customersQuery = useQuery({ queryKey: ['customers'], queryFn: () => api.customers.list({ limit: 100 }) });
  const allDogsQuery = useQuery({ queryKey: ['dogs'], queryFn: () => api.dogs.list({ limit: 100 }) });
  const servicesQuery = useQuery({ queryKey: ['services'], queryFn: () => api.services.list({ limit: 100 }) });

  const appointments = appointmentsQuery.data?.items ?? [];
  const customers = customersQuery.data?.items ?? [];
  const allDogs = allDogsQuery.data?.items ?? [];
  const services = servicesQuery.data?.items ?? [];
  const canManageAppointments = user?.groups.some((role) => role === 'Administrator' || role === 'Receptionist') ?? false;
  const isAdministrator = user?.groups.includes('Administrator') ?? false;

  const filteredAppointments = useMemo(() => appointments.filter((appointment) => {
    const customer = customers.find((item) => item.id === appointment.customerId);
    const dogNames = appointment.dogIds.map((dogId) => allDogs.find((dog) => dog.id === dogId)?.name ?? dogId);
    const serviceNames = appointment.serviceIds.map((serviceId) => services.find((service) => service.id === serviceId)?.name ?? serviceId);
    if (!visibleCategories.includes(appointment.serviceCategory)) return false;
    if (!overlapsDateRange(appointment.scheduledAt, appointment.scheduledAt, filters.from, filters.to)) return false;
    if (statusFilter && appointment.status !== statusFilter) return false;
    if (categoryFilter && appointment.serviceCategory !== categoryFilter) return false;
    return matchesSearch(search, [
      customer?.firstName,
      customer?.lastName,
      `${customer?.firstName ?? ''} ${customer?.lastName ?? ''}`,
      ...dogNames,
      ...serviceNames,
      appointment.notes,
      appointment.id,
      statusLabels[appointment.status],
    ]);
  }), [allDogs, appointments, categoryFilter, customers, filters.from, filters.to, search, services, statusFilter, visibleCategories]);
  const appointmentsPage = useControlledPagination(filteredAppointments, page, (nextPage) => setValue('page', String(nextPage)), pageSize);

  const markDone = useMutation({
    mutationFn: api.appointments.markDone,
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ['appointments'] }),
  });
  const deleteAppointment = useMutation({
    mutationFn: api.appointments.remove,
    onSuccess: async () => queryClient.invalidateQueries({ queryKey: ['appointments'] }),
  });
  const isLoading = appointmentsQuery.isLoading || customersQuery.isLoading || allDogsQuery.isLoading || servicesQuery.isLoading;
  const error = appointmentsQuery.error ?? customersQuery.error ?? allDogsQuery.error ?? servicesQuery.error;

  return (
    <div className="space-y-5">
      <div className="flex flex-col items-start gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-950">{isTransportMode ? 'Transportes' : 'Grooming / estetica'}</h1>
          <p className="mt-1 text-sm text-slate-500">{isTransportMode ? 'Traslados programados por cliente y uno o mas perros.' : 'Servicios programados por cliente y uno o mas perros.'}</p>
        </div>
        {canManageAppointments ? (
          <Link className="inline-flex h-9 items-center gap-2 rounded-md border border-[#d6ded0] bg-white px-3 text-sm font-medium text-[#334033] hover:bg-[#eef3ea]" to={isTransportMode ? '/admin/grooming/new?category=transportation' : '/admin/grooming/new'}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            {isTransportMode ? 'Nuevo transporte' : 'Nueva cita'}
          </Link>
        ) : null}
      </div>

      <Card>
        <CardHeader>
          <div className="grid gap-3">
            <div className="flex flex-col items-start gap-3">
              <h2 className="text-sm font-semibold">Agenda</h2>
              <SearchBox value={search} onChange={(value) => setValue('q', value)} placeholder="Buscar cita" label="Buscar cita" />
            </div>
            <div className="flex flex-wrap gap-2">
              <Select value={statusFilter} onChange={(event) => setValue('status', event.target.value)} aria-label="Filtrar por estado">
                <option value="">Todos los estados</option>
                <option value="scheduled">Programadas</option>
                <option value="done">Terminadas</option>
              </Select>
              {isTransportMode ? null : (
                <Select value={categoryFilter} onChange={(event) => setValue('category', event.target.value)} aria-label="Filtrar por tipo">
                  <option value="">Todos los tipos</option>
                  {visibleCategories.map((category) => <option key={category} value={category}>{labelForServiceCategory(category)}</option>)}
                </Select>
              )}
            </div>
            <ListDateFilters range={filters.range} from={filters.from} to={filters.to} onChange={setValues} startId="appointmentsFrom" endId="appointmentsTo" />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? <LoadingState label="Cargando citas" /> : null}
          {error ? <ErrorState message={error.message} /> : null}
          {appointmentsQuery.data && appointments.length === 0 ? <EmptyState title="Aun no hay citas" body="Crea una cita para programar servicios." /> : null}
          {appointments.length > 0 && filteredAppointments.length === 0 ? <EmptyState title="Sin resultados" body="Intenta con otro cliente, perro, servicio o estado." /> : null}
          {filteredAppointments.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <thead>
                  <tr>
                    <Th>Concepto</Th>
                    <Th>Fecha</Th>
                    <Th>Cliente</Th>
                    <Th>Perros</Th>
                    <Th>Tipo</Th>
                    <Th>Servicios</Th>
                    <Th>Estado</Th>
                    {isAdministrator ? <Th>Terminada por</Th> : null}
                    {canManageAppointments ? <Th>Acciones</Th> : null}
                  </tr>
                </thead>
                <tbody>
                  {appointmentsPage.pageItems.map((appointment) => {
                    const customer = customers.find((item) => item.id === appointment.customerId);
                    const dogNames = appointment.dogIds.map((dogId) => allDogs.find((dog) => dog.id === dogId)?.name ?? dogId);
                    const serviceNames = appointment.serviceIds.map((serviceId) => services.find((service) => service.id === serviceId)?.name ?? serviceId);
                    const conceptId = paymentConceptId('service', appointment.serviceIds[0] ?? appointment.id);
                    return (
                      <tr key={appointment.id}>
                        <Td><span className="font-mono text-xs">{conceptId}</span></Td>
                        <Td>{formatDateTime(appointment.scheduledAt)}</Td>
                        <Td className="font-medium text-slate-900">{customer ? `${customer.firstName} ${customer.lastName}` : appointment.customerId}</Td>
                        <Td>{dogNames.join(', ')}</Td>
                        <Td>{labelForServiceCategory(appointment.serviceCategory)}</Td>
                        <Td>{serviceNames.join(', ')}</Td>
                        <Td><Badge>{statusLabels[appointment.status]}</Badge></Td>
                        {isAdministrator ? <Td>{appointment.completedByName ?? appointment.completedByUserId ?? '-'}</Td> : null}
                        {canManageAppointments ? <Td>
                          <ActionMenu groups={[
                            {
                              label: 'Pago',
                              children: <MenuLink disabled={appointment.serviceIds.length === 0} to={`/admin/payments?customerId=${encodeURIComponent(appointment.customerId)}&sourceType=service&sourceId=${encodeURIComponent(appointment.id)}`} icon={<CreditCard className="h-4 w-4" aria-hidden="true" />}>Ir a pagos</MenuLink>,
                            },
                            {
                              label: 'Flujo',
                              children: <>
                                <MenuLink to={`/admin/grooming/${appointment.id}/edit`} icon={<Pencil className="h-4 w-4" aria-hidden="true" />}>Editar cita</MenuLink>
                                <MenuButton disabled={appointment.status === 'done' || markDone.isPending} onClick={() => markDone.mutate(appointment.id)} icon={<CheckCircle2 className="h-4 w-4" aria-hidden="true" />}>Marcar terminada</MenuButton>
                              </>,
                            },
                            {
                              label: 'Riesgo',
                              tone: 'danger',
                              children: <MenuButton danger disabled={deleteAppointment.isPending} onClick={() => setConfirm({ title: 'Eliminar cita', body: `Eliminar la cita de ${dogNames.join(', ')}?`, confirmLabel: 'Eliminar', onConfirm: () => deleteAppointment.mutate(appointment.id) })} icon={<Trash2 className="h-4 w-4" aria-hidden="true" />}>Eliminar</MenuButton>,
                            },
                          ]} />
                        </Td> : null}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          ) : null}
          <PaginationControls {...appointmentsPage} onPageChange={appointmentsPage.setPage} onPageSizeChange={(nextPageSize) => setValues({ pageSize: String(nextPageSize), page: '1' })} />
          {markDone.isError ? <p className="mt-3 text-sm text-rose-600">{markDone.error.message}</p> : null}
          {deleteAppointment.isError ? <p className="mt-3 text-sm text-rose-600">{deleteAppointment.error.message}</p> : null}
        </CardContent>
      </Card>
      <ConfirmDialog state={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
}

function paymentConceptId(sourceType: 'stay' | 'daycare' | 'service', sourceId: string) {
  const prefix = sourceType === 'stay' ? 'EST' : sourceType === 'daycare' ? 'GUA' : 'SER';
  return `${prefix}-${sourceId.replace(/[^a-z0-9]/gi, '').slice(-6).toUpperCase()}`;
}

function MenuLink({ to, disabled, icon, children }: { readonly to: string; readonly disabled?: boolean; readonly icon: ReactNode; readonly children: ReactNode }) {
  return <Link className={`flex items-center gap-2 rounded-md px-2 py-2 ${disabled ? 'pointer-events-none text-[#9aa392]' : 'text-[#334033] hover:bg-[#eef3ea]'}`} to={to}>{icon}{children}</Link>;
}

function MenuButton({ children, danger, disabled, icon, onClick }: { readonly children: ReactNode; readonly danger?: boolean; readonly disabled?: boolean; readonly icon: ReactNode; readonly onClick: () => void }) {
  return <button type="button" className={`flex items-center gap-2 rounded-md px-2 py-2 text-left disabled:cursor-not-allowed disabled:opacity-50 ${danger ? 'text-[#8a4a3a] hover:bg-[#fff8f8]' : 'text-[#334033] hover:bg-[#eef3ea]'}`} disabled={disabled} onClick={onClick}>{icon}{children}</button>;
}
